import { type RefObject, useLayoutEffect, useRef } from 'react';

import { isFieldSizingSupported } from '../utils/is_field_sizing_supported';

const resizeToContent = (element: HTMLTextAreaElement) => {
  const { borderTopWidth, borderBottomWidth } = getComputedStyle(element);
  const borderHeight =
    parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);

  // Reset the height first so the textarea can shrink, then grow it to fit the
  // content. `scrollHeight` excludes the border, which the box-sizing:border-box
  // height needs to include, so the border height is added back.
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight + borderHeight}px`;
};

/**
 * Provides JavaScript-based auto-sizing for browsers that don't support the CSS
 * `field-sizing: content` property (e.g. Firefox and Safari). Returns a ref to
 * attach to the textarea when the fallback is active, or `undefined` when the
 * browser handles sizing natively and no ref is needed.
 */
export function useFieldSizingFallback(
  enabled: boolean,
  value?: string,
): RefObject<HTMLTextAreaElement | null> | undefined {
  const ref = useRef<HTMLTextAreaElement>(null);
  const isFallbackActive = enabled && !isFieldSizingSupported();

  useLayoutEffect(() => {
    const element = ref.current;
    if (!isFallbackActive || element === null || value !== undefined) {
      return;
    }

    // Ensure an initial correct size for uncontrolled textarea components (e.g. with defaultValue).
    resizeToContent(element);

    const handleResize = () => resizeToContent(element);
    element.addEventListener('input', handleResize);
    return () => element.removeEventListener('input', handleResize);
  }, [isFallbackActive, value]);

  useLayoutEffect(() => {
    if (isFallbackActive && ref.current !== null && value !== undefined) {
      resizeToContent(ref.current);
    }
  }, [isFallbackActive, value]);

  return isFallbackActive ? ref : undefined;
}
